import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect } from "react";
import { ActivityIndicator, View } from "react-native";

import { getClientCompetition } from "../../../../../lib/clientCompetitions";
import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Client;

// The bare /client/competition/[id] URL doesn't render any UI — it
// redirects to the right child page based on the case's status:
//
//   Pre-approval (the case is still being shaped by the client or
//   reviewed by Coravu):
//     draft / denied / denied_terminal / submitted / under_review
//     → /edit  (the case editor is where the work happens)
//
//   Approved or later (case has been approved by Coravu — quote,
//   contract, payment, and live phases):
//     approved / contract_pending / contract_signed / payment_pending /
//     open / judging / closed / final_invoice_pending / final_invoice_paid
//     → /dashboard  (the case overview — stats, timeline, status)
//
// The dashboard handles all post-approval states gracefully (zeros for
// stats during the contract/payment window, fills in as students
// register once status hits `open`).
//
// We need to fetch the case row to read its status before redirecting —
// a small spinner shows while it lands.
export default function CompetitionIndexRedirect() {
  const { id } = useLocalSearchParams<{ id: string }>();

  useEffect(() => {
    if (typeof id !== "string" || id.length === 0) return;
    let cancelled = false;
    void (async () => {
      const row = await getClientCompetition(id).catch(() => null);
      if (cancelled) return;
      const status = row?.status ?? "draft";
      const dashboardStatuses = [
        "approved",
        "contract_pending",
        "contract_signed",
        "payment_pending",
        "open",
        "judging",
        "closed",
        "final_invoice_pending",
        "final_invoice_paid",
      ];
      const target = dashboardStatuses.includes(status)
        ? `/client/competition/${id}/dashboard`
        : `/client/competition/${id}/edit`;
      router.replace(target as Href);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
      <ActivityIndicator size="small" color={THEME.accent} />
    </View>
  );
}
